/**
 * Client-side image preparation.
 *
 * Photos are downscaled and re-encoded as JPEG before they leave the phone.
 * The old flow uploaded full-resolution canvas PNGs — several megabytes each,
 * for no analytical benefit, over a connection that is often a customer
 * standing in a workshop on one bar of signal.
 *
 * 1600px on the long edge is comfortably above what the damage detection
 * models need and keeps a typical photo under 400KB.
 */

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.82;

/**
 * Decode to a bitmap, honouring EXIF orientation where the browser supports it.
 *
 * Accepts a Blob/File or a data URL. The string case exists because
 * `URL.createObjectURL` throws "Argument 1 could not be converted to any of:
 * Blob, MediaSource" on a string, which is a baffling error to meet at the end
 * of taking a photo.
 */
async function decode(source) {
  const isDataUrl = typeof source === 'string';

  if (!isDataUrl && typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(source, { imageOrientation: 'from-image' });
    } catch {
      // Safari < 17 rejects the options bag — fall through to the <img> path.
    }
  }

  const url = isDataUrl ? source : URL.createObjectURL(source);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('That file could not be read as an image'));
      img.src = url;
    });
  } finally {
    if (!isDataUrl) URL.revokeObjectURL(url);
  }
}

function scaledSize(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * @param {Blob|File} source
 * @returns {Promise<{dataUrl: string, width: number, height: number, bytes: number}>}
 */
export async function compressImage(source, { maxEdge = DEFAULT_MAX_EDGE, quality = DEFAULT_QUALITY } = {}) {
  const bitmap = await decode(source);
  const { width, height } = scaledSize(
    bitmap.width || bitmap.naturalWidth,
    bitmap.height || bitmap.naturalHeight,
    maxEdge
  );

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const dataUrl = canvas.toDataURL('image/jpeg', quality);

  return {
    dataUrl,
    width,
    height,
    // base64 is 4 chars per 3 bytes; close enough for a size guard.
    bytes: Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75)
  };
}

/**
 * Grab the current frame of a live <video> as a Blob.
 *
 * Deliberately NOT compressed here: the upload path compresses everything it
 * is handed, and doing it twice loses quality for no reason. Returning a Blob
 * also means camera captures and file picks are the same kind of thing from
 * this point on.
 */
export async function captureFromVideo(video) {
  if (!video?.videoWidth || !video?.videoHeight) {
    throw new Error('The camera is not ready yet');
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  if (!blob) throw new Error('The photo could not be saved');
  return blob;
}

/** Reject the obvious non-images before spending a decode on them. */
export function isSupportedImage(file) {
  return Boolean(file) && /^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type || '');
}
